import { useStableCallback } from '@a-type/ui';
import { useEffect, useRef } from 'react';
import { useWebHaptics } from 'web-haptics/react';
import { boundsRegistry, useTagBounds } from './bounds.js';
import { draggableDataRegistry } from './dataRegistry.js';
import { dndEvents } from './dndEvents.js';
import { useDndStore, useDraggedData } from './dndStore.js';
import { gesture } from './gestureStore.js';
import { dndLogger } from './logger.js';
import { TAGS } from './tags.js';
const defaultAccept = () => true;
const defaultTags = [TAGS.DROPPABLE, TAGS.DRAG_INTERACTIVE];
export function useDroppable({ onDrop, accept, onReject, id, onOver, disabled, tags = defaultTags, }) {
    const dropCb = useStableCallback(onDrop);
    const stableAccept = useStableCallback(accept || defaultAccept);
    const stableOnReject = useStableCallback(onReject);
    const { trigger: buzz } = useWebHaptics();
    useEffect(() => {
        if (disabled)
            return;
        return dndEvents.subscribe('drop', (dragged, targetId, gesture) => {
            if (targetId === id) {
                dndLogger.info('droppable drop', id);
                const data = draggableDataRegistry.get(dragged);
                if (stableAccept({ id: dragged, data }, gesture)) {
                    const region = boundsRegistry.getEntry(id);
                    const dropInfo = {
                        relativePosition: {
                            x: gesture.currentRaw.x - region.bounds.x,
                            y: gesture.currentRaw.y - region.bounds.y,
                        },
                        droppableRect: region.bounds,
                    };
                    dropCb({ id: dragged, data }, gesture, dropInfo);
                    buzz([{ duration: 30 }, { delay: 60, duration: 40, intensity: 1 }]);
                }
                else if (stableOnReject) {
                    stableOnReject({ id: dragged, data }, gesture);
                }
            }
        });
    }, [id, dropCb, stableAccept, stableOnReject, disabled]);
    const isAnyOver = useDndStore((state) => state.overRegion === id) && !disabled;
    const draggedData = useDraggedData();
    const isAcceptedOver = !disabled &&
        isAnyOver &&
        draggedData &&
        (!accept || accept(draggedData, gesture));
    const isRejectedOver = !disabled && isAnyOver && draggedData && !isAcceptedOver;
    const wasOverRef = useRef(false);
    useEffect(() => {
        if (isAcceptedOver && !wasOverRef.current) {
            dndEvents.emit('over', id);
            wasOverRef.current = true;
        }
        else if (!isAcceptedOver && wasOverRef.current) {
            dndEvents.emit('out');
            wasOverRef.current = false;
        }
    }, [isAcceptedOver, id]);
    const stableOnOver = useStableCallback(onOver);
    const unvalidatedOver = isAnyOver ? draggedData : null;
    useEffect(() => {
        // Call the stable callback with
        stableOnOver?.(unvalidatedOver, gesture);
    }, [stableOnOver, unvalidatedOver]);
    useTagBounds(id, tags);
    return {
        isAcceptedOver,
        isAnyOver,
        isRejectedOver,
        draggedData,
    };
}
//# sourceMappingURL=useDroppable.js.map